import { useMemo, useRef } from "react";
import { Box } from "@chakra-ui/react";
import { v2ApiNewHireJourneyPathById } from "helpers/url/api/v2";
import { useForm } from "react-hook-form";
import { useCurrentCompanyAdminQuery } from "features/Company/hooks";
import { useFormDetailQuery } from "features/Form/hooks";
import LoadingDots from "components/Loading";
import { useQueryClient } from "@tanstack/react-query";
import { newHireJourneyKeys } from "features/NewHireJourney/hooks";
import { useFeature } from "hooks/useFeature";
import AutoSaveForm from "./AutoSaveForm";
import { applyExtendedRoleVisibility, getPendingFormSections } from "./pendingNewHireForm";

interface PendingNewHireFormProps {
  newHireJourneyId: string;
  form: ReturnType<typeof useForm>;
  isLoaded: boolean;
}

export default function PendingNewHireForm({ form, newHireJourneyId, isLoaded }: PendingNewHireFormProps) {
  const queryClient = useQueryClient();
  const containerRef = useRef<HTMLDivElement>(null);
  const { data: company } = useCurrentCompanyAdminQuery();
  const { data: pendingNewHireForm } = useFormDetailQuery(company?.pendingNewHireFormId as string, {
    enabled: !!company?.pendingNewHireFormId,
  });
  const { enabled: extendedRolesEnabled } = useFeature("extended_journey_roles");
  const defaultSections = useMemo(() => getPendingFormSections({ extendedRolesEnabled }), [extendedRolesEnabled]);
  const customSections = useMemo(
    () => applyExtendedRoleVisibility(pendingNewHireForm?.schema?.sections || [], extendedRolesEnabled),
    [pendingNewHireForm?.schema?.sections, extendedRolesEnabled],
  );

  return (
    <Box width="100%" height="calc(100vh - 96px)" maxWidth="700px">
      <Box ref={containerRef} position="relative">
        {company && !company.pendingNewHireFormId ? (
          <AutoSaveForm
            form={form}
            sections={defaultSections}
            isLoaded={isLoaded}
            submitUrl={v2ApiNewHireJourneyPathById(newHireJourneyId || "")}
            onSuccess={() => queryClient.invalidateQueries({ queryKey: newHireJourneyKeys.detail(newHireJourneyId) })}
            size="sm"
          />
        ) : !pendingNewHireForm ? (
          <Box mt="16">
            <LoadingDots />
          </Box>
        ) : (
          <AutoSaveForm
            form={form}
            sections={customSections}
            isLoaded={isLoaded}
            submitUrl={v2ApiNewHireJourneyPathById(newHireJourneyId || "")}
            size="sm"
          />
        )}
      </Box>
    </Box>
  );
}
